/**
 * UI-only store for the message drawer's open state + current target.
 *
 *   - target = null         → drawer closed
 *   - target = 'new'        → "NEW MESSAGE" composer (no thread yet)
 *   - target = '<threadId>' → existing thread view
 *
 * The drawer lives in AppLayout, so any surface (ClinicScreen's "New
 * message" CTA, a ThreadPreview row tap, a deep-link redirect from
 * ThreadDetail) can drive it without prop-drilling.
 */

import { useSyncExternalStore } from 'react';

type Target = 'new' | string | null;

let target: Target = null;
const listeners = new Set<() => void>();

// Care-team messaging experiment: the unified Messages drawer registers
// itself here so every "message your clinician" path lands on its
// Providers team instead of this drawer.
let delegate: (() => void) | null = null;

function emit() {
  listeners.forEach((l) => l());
}

export const messageDrawer = {
  openNew() {
    if (delegate) return delegate();
    if (target === 'new') return;
    target = 'new';
    emit();
  },
  openThread(threadId: string) {
    if (delegate) return delegate();
    if (target === threadId) return;
    target = threadId;
    emit();
  },
  setDelegate(next: (() => void) | null) {
    delegate = next;
    if (next && target !== null) {
      target = null;
      emit();
    }
  },
  close() {
    if (target === null) return;
    target = null;
    emit();
  },
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useMessageDrawerTarget(): Target {
  return useSyncExternalStore(subscribe, () => target);
}
